import { CommonModule } from '@angular/common';
import { Component, effect, inject, input, output, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { RemoteAction } from '@devices/models';
import { DeviceRemoteActionService } from '@devices/services';
import { NotificationsService } from '@ui';
import { finalize } from 'rxjs';

import { ExecuteActionDialogComponent } from '../execute-action-dialog';
import { RemoteActionSettingsDialogComponent } from '../remote-action-settings-dialog';

@Component({
  imports: [MatProgressSpinner, MatIcon, CommonModule],
  selector: 'avs-remote-action-list',
  styleUrl: './remote-action-list.component.scss',
  templateUrl: './remote-action-list.component.html',
})
export class RemoteActionListComponent {
  deviceId = input.required<string>();
  twoWayCommunication = input(false);
  editable = input(true);
  remoteActionsChanged = output();

  protected remoteActionService = inject(DeviceRemoteActionService);
  protected remoteActionList = signal<RemoteAction[]>([]);
  protected loading = signal(false);
  private dialog = inject(MatDialog);
  private notificationService = inject(NotificationsService);

  constructor() {
    effect(() => {
      this.fetchRemoteActionList();
    });
  }

  fetchRemoteActionList(showLoader = true) {
    if (showLoader) {
      this.loading.set(true);
    }
    this.remoteActionService.remoteActionList(this.deviceId())
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        error: (err) => {
          this.notificationService.showErrorMessage(err.message);
        },
        next: (actionList) => {
          this.remoteActionList.set(actionList);
        },
      });
  }

  openRemoteActionMenu(remoteAction: RemoteAction | null = null) {
    this.dialog.open(RemoteActionSettingsDialogComponent, {
      backdropClass: 'backdrop-modal-panel',
      data: { deviceId: this.deviceId(), remoteAction },
      disableClose: true,
      height: '60vh',
      maxHeight: '60vh',
      width: '70vw',
    }).afterClosed().subscribe((result: { fetch: boolean; }) => {
      if (result?.fetch) {
        this.fetchRemoteActionList(false);
        this.remoteActionsChanged.emit();
      }
    });
  }

  executeAction(action: RemoteAction) {
    this.dialog.open(ExecuteActionDialogComponent, {
      backdropClass: 'backdrop-modal-panel',
      data: {
        actionName: action.name,
        actionPoint: action.deviceActionPoint,
        deviceId: this.deviceId(),
        isRemoteAction: true,
        remoteActionPointId: action.remoteDeviceActionPointId,
      },
      disableClose: true,
    });
  }
}
